import styled from "@emotion/styled"
import { htmlFormatTags, Value } from "@stringtale/react"
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { useCurrentOrganization } from "app/core/hooks/useCurrentOrganization"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import usePopoverSeen from "app/core/hooks/usePopoverSeen"
import { useRouter } from "app/core/hooks/useRouter"
import { BlueprintLabel } from "app/schooljaren/Layout"
import ExclamationChatbubbleIcon from "app/home/assets/exclamation-chatbubble.svg"
import WarningCircleIcon from "app/home/components/WarningCircleIcon"
import type { BlueprintBannerState } from "app/schooljaren/components/SchoolManagementSchoolyearBanner"
import type { GetBlueprintResult } from "app/schooljaren/queries/getBlueprint"
import { useHasSchoolyears } from "app/schooljaren/hooks/useHasSchoolyears"
import { MODAL_QUERY_ARCHIVE_KEY } from "app/playlists/consts"
import { trpc } from "app/trpc"
import { isSchoolAdminRole } from "app/users/utils/schoolAdminRoles"
import { format, isBefore } from "date-fns"
import { nl } from "date-fns/locale"
import { useQueryState } from "nuqs"
import type { ReactNode } from "react"
import { useState } from "react"
import { DARK_BLUE_COLOR, GRAY_100_COLOR } from "theme/colors"
import { PX13 } from "theme/sizes"
import Button from "ui/Button"
import Group from "ui/Group"
import Stack from "ui/Stack"
import TextLink from "ui/TextLink"
import Title from "ui/Title"

const RED_600 = "var(--color-red-600)"

const Root = styled.div`
  display: flex;
  filter: drop-shadow(0px 0px 5px rgba(0, 0, 0, 0.2));
  margin-bottom: 40px;
  width: 67%;
  box-sizing: border-box;

  @container (max-width: 1023px) {
    width: 100%;
  }
`

const AccentBar = styled.div`
  width: 12px;
  flex-shrink: 0;
  align-self: stretch;
  background-color: ${RED_600};
  border: 1px solid ${GRAY_100_COLOR};
  border-right: none;
  border-radius: 16px 0 0 16px;
`

const Content = styled(Stack)`
  flex: 1;
  min-width: 0;
  background-color: white;
  border: 1px solid ${GRAY_100_COLOR};
  border-left: none;
  border-radius: 0 16px 16px 0;
  padding: 14px 16px 8px;
  overflow: hidden;
`

const Header = styled(Group)`
  gap: 6px;
  padding-left: 4px;
  align-items: center;
`

const HeaderIcon = styled.img`
  width: 20px;
  height: 18px;
  flex-shrink: 0;
  display: block;
`

const TablerIcon = styled.span<{ size: number; color: string }>`
  display: inline-flex;
  flex-shrink: 0;
  width: ${(p) => p.size}px;
  height: ${(p) => p.size}px;
  color: ${(p) => p.color};

  svg {
    width: 100%;
    height: 100%;
  }
`

function TablerSpriteIcon({
  name,
  color,
  size,
}: {
  name: string
  color: string
  size: number
}) {
  return (
    <TablerIcon color={color} size={size}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {name === "x" ? (
          <>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </>
        ) : null}
      </svg>
    </TablerIcon>
  )
}

const NotificationList = styled(Stack)`
  width: 100%;
`

const NotificationRow = styled(Group)`
  width: 100%;
  padding: 8px 4px;
  border-top: 1px solid ${GRAY_100_COLOR};
  align-items: center;
  gap: 16px;
`

const RowContent = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 4px;
  align-items: center;
`

const MessageContent = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  flex: 1;
  min-width: 0;
  line-height: 1.25;
  font-size: ${PX13};
  color: ${DARK_BLUE_COLOR};
`

const ActionLink = styled(TextLink)`
  && {
    border: none;
    background: none;
    font-family: inherit;
    padding: 0;
  }
`

const CloseButton = styled(Button)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  padding: 0;
`

function HomepageNotificationRow({
  message,
  buttonText,
  buttonAction,
  onCloseAction,
  urgent = false,
}: {
  message: ReactNode
  buttonText: ReactNode
  buttonAction: () => void
  onCloseAction?: () => void
  urgent?: boolean
}) {
  const iconColor = urgent ? RED_600 : DARK_BLUE_COLOR

  return (
    <NotificationRow align="center" justify="space-between">
      <RowContent>
        <WarningCircleIcon color={iconColor} />
        <MessageContent>
          {message}
          <ActionLink
            as="button"
            type="button"
            inline
            bold
            size={PX13}
            onClick={buttonAction}
          >
            {buttonText}
          </ActionLink>
        </MessageContent>
      </RowContent>
      {onCloseAction ? (
        <CloseButton onClick={onCloseAction} aria-label="Sluiten">
          <TablerSpriteIcon name="x" color={DARK_BLUE_COLOR} size={14} />
        </CloseButton>
      ) : null}
    </NotificationRow>
  )
}

const shouldShowManagerBlueprintInfo = function ({
  hasSchoolyears,
  blueprint,
  isSchoolAdmin,
}: {
  isSchoolAdmin: boolean
  hasSchoolyears: boolean
  blueprint: GetBlueprintResult
}) {
  if (!hasSchoolyears) return false

  return (
    hasSchoolyears &&
    isSchoolAdmin &&
    blueprint &&
    (!blueprint.blueprints[0] || blueprint.blueprints[0].state === "CONCEPT")
  )
}

export default function HomepageNotificationCenter({
  blueprint,
}: {
  blueprint: GetBlueprintResult
}) {
  const currentUser = useCurrentUser()
  const currentSchool = useCurrentOrganization()
  const hasSchoolyears = useHasSchoolyears()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [_isArchiveOpen, setOpenArchive] = useQueryState(
    MODAL_QUERY_ARCHIVE_KEY
  )

  const { data: migrationBanner } = useSuspenseQuery({
    ...trpc.schooljaren.getMigrationBanner.queryOptions(),
  })

  const { data: newSchoolyearStarted } = useSuspenseQuery({
    ...trpc.schooljaren.shouldShowNewSchoolyearInfo.queryOptions(),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  const { data: currentSchoolyear } = useSuspenseQuery({
    ...trpc.schooljaren.getCurrentYear.queryOptions(),
    refetchOnReconnect: false,
  })

  const closeGroupMessageMutation = useMutation(
    trpc.schooljaren.closeGroupMessage.mutationOptions()
  )

  const { state: stateNewSchoolyearBanner, toggle: toggleNewSchoolyearBanner } =
    usePopoverSeen("NEW_SCHOOLYEAR")

  const {
    state: stateNewInitiatorAnnouncement,
    toggle: toggleNewInitiatorAnnouncement,
  } = usePopoverSeen("NEW_INITIATOR_ANNOUNCEMENT")

  // Temporary test messages — can be used for visual QA
  // const [dismissedTestMessages, setDismissedTestMessages] = useState<
  //   Set<string>
  // >(new Set())
  // const testMessages = [
  //   {
  //     id: "test-1",
  //     message: "Testbericht 1: Dit is een voorbeeldnotificatie.",
  //     buttonText: "Bekijken",
  //   },
  //   {
  //     id: "test-2",
  //     message:
  //       "Testbericht 2: Nog een dismissable regel om de layout te checken.",
  //     buttonText: "Openen",
  //   },
  //   {
  //     id: "test-3",
  //     message:
  //       "Testbericht 3: Zo ziet het centrum eruit met meerdere berichten.",
  //     buttonText: "Ga verder",
  //   },
  // ].filter((message) => !dismissedTestMessages.has(message.id))

  const isSchoolAdmin = isSchoolAdminRole({
    currentUser,
    organizationId: currentSchool?.id,
  })

  const userCreatedInCurrentYear =
    currentUser &&
    currentUser.role !== "ADMIN" &&
    currentSchoolyear &&
    currentUser.createdAt >= currentSchoolyear.yearStartDate &&
    currentUser.createdAt <= currentSchoolyear.yearEndDate

  const showMigrationBanner =
    !!migrationBanner && !!newSchoolyearStarted && !userCreatedInCurrentYear

  const showBlueprintBanner = shouldShowManagerBlueprintInfo({
    hasSchoolyears,
    blueprint,
    isSchoolAdmin,
  })

  const showNewSchoolyearManagerInfo =
    !!newSchoolyearStarted &&
    stateNewSchoolyearBanner === "OPENED" &&
    isSchoolAdmin &&
    !userCreatedInCurrentYear

  const showNewInitiatorAnnouncement =
    stateNewInitiatorAnnouncement === "OPENED" &&
    isSchoolAdmin &&
    isBefore(new Date(), new Date(2026, 9, 1))

  const blueprintState: BlueprintBannerState | null = blueprint
    ? blueprint.blueprints.length === 0
      ? "NEW"
      : blueprint.blueprints[0].state === "CONCEPT"
        ? "CONCEPT"
        : "PUBLISHED"
    : null

  const showBlueprintNotification =
    showBlueprintBanner &&
    blueprint &&
    (blueprintState === "NEW" || blueprintState === "CONCEPT")

  if (
    !showMigrationBanner &&
    !showBlueprintNotification &&
    !showNewSchoolyearManagerInfo &&
    !showNewInitiatorAnnouncement
    // testMessages.length === 0
  ) {
    return null
  }

  return (
    <Root>
      <AccentBar />
      <Content align="stretch" gap="12px">
        <Header align="center">
          <HeaderIcon src={ExclamationChatbubbleIcon.src} alt="" aria-hidden />
          <Title
            size="1.0625rem"
            weight="900"
            color={RED_600}
            style={{ lineHeight: 1.25, margin: 0 }}
          >
            <Value name="apps.web.src.home.components.homepagenotificationcenter.belangrijke_berichten">
              Belangrijke berichten
            </Value>
          </Title>
        </Header>

        <NotificationList align="stretch" gap="0">
          {showNewInitiatorAnnouncement ? (
            <HomepageNotificationRow
              urgent
              buttonText={
                <Value name="apps.web.pages.index.new_initiator_announcement_close_button">
                  Lees meer
                </Value>
              }
              buttonAction={async () => {
                router.push("/schoolbeheer/gebruikers")
                await toggleNewInitiatorAnnouncement("CLOSED")
              }}
              onCloseAction={async () => {
                await toggleNewInitiatorAnnouncement("CLOSED")
              }}
              message={
                <Value
                  name="apps.web.pages.index.new_initiator_announcement"
                  format={{
                    ...htmlFormatTags,
                  }}
                >
                  {`De Kartrekker rol is veranderd. Lees meer over deze veranderingen
              en <b>selecteer jullie Kartrekker(s)</b>.`}
                </Value>
              }
            />
          ) : null}

          {showMigrationBanner ? (
            <HomepageNotificationRow
              buttonText={
                <Value name="apps.web.pages.index.open_archief">
                  Open archief
                </Value>
              }
              buttonAction={async () => {
                await setOpenArchive("")
              }}
              onCloseAction={async () => {
                await closeGroupMessageMutation.mutateAsync({
                  type: "MIGRATE_BANNER",
                })
                await queryClient.invalidateQueries({
                  queryKey:
                    trpc.schooljaren.getMigrationBanner.queryKey(undefined),
                })
              }}
              message={
                <Value name="apps.web.pages.index.wil_je_schoollijsten_van_vorig_jaar_gebruiken">
                  Wil je de lijsten van vorig jaar bekijken?
                </Value>
              }
            />
          ) : null}

          {showBlueprintNotification &&
          blueprint &&
          blueprintState === "NEW" ? (
            <HomepageNotificationRow
              urgent
              buttonText={
                <Value name="apps.web.src.schooljaren.components.homepagebanner.start_met_de_schooljaarovergang">
                  Start
                </Value>
              }
              buttonAction={async () => {
                await router.push("/schoolbeheer/schooljaren/stap-1")
              }}
              message={
                <Value
                  format={{
                    ...htmlFormatTags,
                    year: blueprint.displayTitle,
                    launchDate: format(blueprint.yearStartDate, "d MMMM", {
                      locale: nl,
                    }),
                  }}
                  name="apps.web.src.schooljaren.components.homepagebanner.actie_vereist"
                >
                  {`Actie vereist vóór <b>{launchDate}</b>: Schooljaarovergang {year}`}
                </Value>
              }
            />
          ) : null}

          {showBlueprintNotification &&
          blueprint &&
          blueprintState === "CONCEPT" ? (
            <HomepageNotificationRow
              urgent
              buttonText={
                <Value name="apps.web.src.schooljaren.components.homepagebanner.voltooi_de_schooljaarovergang">
                  Voltooi
                </Value>
              }
              buttonAction={async () => {
                await router.push("/schoolbeheer/schooljaren/stap-2")
              }}
              message={
                <>
                  <Value
                    format={{
                      ...htmlFormatTags,
                      year: blueprint.displayTitle,
                      launchDate: format(blueprint.yearStartDate, "d MMMM", {
                        locale: nl,
                      }),
                    }}
                    name="apps.web.src.schooljaren.components.homepagebanner.voltooi_concept"
                  >
                    {`Voltooi vóór <b>{launchDate}</b>: Schooljaarovergang {year}`}
                  </Value>
                  <BlueprintLabel blueprint={blueprint}>
                    <Value name="apps.web.src.schooljaren.layout.label.concept">
                      {`Concept`}
                    </Value>
                  </BlueprintLabel>
                </>
              }
            />
          ) : null}

          {showNewSchoolyearManagerInfo ? (
            <HomepageNotificationRow
              buttonText={
                <Value name="apps.web.pages.index.new_schoolyear_close_button">
                  Aan de slag!
                </Value>
              }
              buttonAction={async () => {
                await router.push("/schoolbeheer")
              }}
              onCloseAction={async () => {
                await toggleNewSchoolyearBanner("CLOSED")
              }}
              message={
                currentSchool?.schoolyearResult === "BLUEPRINT_USED" ? (
                  <Value name="apps.web.pages.index.controleer_de_leerkrachtaccounts_voor_het_nieuwe_schooljaar">
                    Schoolbeheer: Controleer de leerkrachtaccounts voor het
                    nieuwe schooljaar.
                  </Value>
                ) : (
                  <Value name="apps.web.pages.index.controleer_de_groepsindeling_en_de_leerkrachtaccounts">
                    Schoolbeheer: Controleer de groepsindeling en de
                    leerkrachtaccounts.
                  </Value>
                )
              }
            />
          ) : null}

          {/* {testMessages.map((testMessage) => (
            <HomepageNotificationRow
              key={testMessage.id}
              message={testMessage.message}
              buttonText={testMessage.buttonText}
              buttonAction={() => undefined}
              onCloseAction={() => {
                setDismissedTestMessages((prev) =>
                  new Set(prev).add(testMessage.id)
                )
              }}
            />
          ))} */}
        </NotificationList>
      </Content>
    </Root>
  )
}
